import type { AuthProviderProps } from 'react-oidc-context';
import { WebStorageStateStore } from 'oidc-client-ts';

import type { AdminRuntimeConfig } from '../../runtime-config';

/**
 * Build the react-oidc-context provider props from runtime config
 * (admin-shell-oidc-rbac B2/LD-2). Authorization-Code + PKCE against a PUBLIC
 * admin client — NO `client_secret` in the browser. `oidc-client-ts` does S256
 * PKCE by default for `response_type=code`. Silent renew keeps the session alive
 * without a full re-login.
 */
export function buildOidcProps(config: AdminRuntimeConfig): AuthProviderProps {
  return {
    authority: config.issuer,
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    post_logout_redirect_uri: config.postLogoutRedirectUri,
    response_type: 'code',
    scope: config.scope,
    automaticSilentRenew: true,
    // Persist the session across reloads/tabs; tokens still live only in the
    // browser (public client, no secret).
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    // Strip `?code&state` from the URL after a successful login so a refresh
    // doesn't replay the (now-consumed) authorization code. The path itself is
    // still `/callback` here — RequireAuth restores the pre-login location via
    // a router <Navigate> (a bare replaceState would not notify the router).
    onSigninCallback: () => {
      window.history.replaceState({}, document.title, window.location.pathname);
    },
  };
}

/**
 * Auth-error sources that mean "the session ended" — recoverable by bouncing
 * through the login again — as opposed to "auth is misconfigured", where a
 * redirect would just loop.
 *
 * Classified on react-oidc-context's `source` discriminant and NOT the message:
 * Keycloak's wording for a dead session varies ("Token is not active",
 * "Session not active", "Invalid refresh token") so sniffing text is brittle.
 * `signinCallback`/`signinRedirect` failures deliberately stay NON-recoverable —
 * those are the ones that would loop, and they keep the error banner.
 *
 * `signinSilent` is included for robustness though nothing in the admin app
 * calls `signinSilent()` directly today; `automaticSilentRenew` surfaces as
 * `renewSilent`.
 */
const RECOVERABLE_ERROR_SOURCES = new Set(['renewSilent', 'signinSilent']);

/**
 * Does this `auth.error` mean the session simply expired?
 *
 * Takes `unknown` deliberately: a narrower `{ source?: string }` compiles
 * against `auth.error` but breaks the tests, because TypeScript
 * excess-property-checks fresh object literals — a direct call like
 * `isExpiredSessionError({ source: 'renewSilent', message: '…' })` would fail on
 * the surplus `message`.
 */
export function isExpiredSessionError(error: unknown): boolean {
  const source = (error as { source?: unknown } | null | undefined)?.source;
  return typeof source === 'string' && RECOVERABLE_ERROR_SOURCES.has(source);
}

/**
 * Post-login restore target from the OIDC round-trip `state` (RequireAuth
 * passes `{ returnTo }` into `signinRedirect`). Only same-app absolute paths
 * are honoured — no host-relative `//…` or absolute URLs (open-redirect
 * hygiene), and never `/callback` itself (it would loop). Falls back to `/`.
 */
export function returnToFromState(state: unknown): string {
  if (typeof state === 'object' && state !== null && 'returnTo' in state) {
    const raw = (state as { returnTo: unknown }).returnTo;
    if (
      typeof raw === 'string' &&
      raw.startsWith('/') &&
      !raw.startsWith('//') &&
      raw !== '/callback' &&
      !raw.startsWith('/callback?')
    ) {
      return raw;
    }
  }
  return '/';
}
